import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getLatestOperatorRunEndAt } from 'lib/selectors/getLatestOperatorRunEndAt';
import { getShiftStart } from 'lib/selectors/getShiftStart';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import _get from 'lodash/get';
import { safeIsBefore, toISO, subtract } from 'lib/utils/date';

import { getCompany } from 'lib/selectors/getCompany';
import { TickerContext } from 'components/TickerProvider';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import PinVerificationModal from 'components/PinVerificationModal';

const OPTION_IDS = {
  NOW: 'now',
  LAST_SIGN_OUT: 'last-sign-out',
  START_OF_SHIFT: 'start-of-shift',
};

const LaborTicketSignInModal = ({ operator, onClose, onSubmit, isOpen }) => {
  const { t } = useTranslation();

  const nowMs = useContext(TickerContext);

  // Get timezone from company
  const company = useSelector(getCompany);
  const latestOperatorRunEndAt = useSelector(getLatestOperatorRunEndAt);
  const shiftStart = useSelector(getShiftStart);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);

  const tzid = _get(company, 'timezone');

  // PIN verification state
  const [showPinVerification, setShowPinVerification] = useState(false);
  const [pendingSubmitOpts, setPendingSubmitOpts] = useState(null);

  const optionsList = useMemo(() => {
    const twentyFourHoursAgoISO = subtract(nowMs, 24, 'hours');
    const options = [
      {
        id: OPTION_IDS.NOW,
        label: t('Now'),
        value: toISO(nowMs),
        disabled: false,
      },
    ];

    const latestOperatorRunEndAtOptionDisabled =
      !latestOperatorRunEndAt ||
      safeIsBefore(latestOperatorRunEndAt, twentyFourHoursAgoISO);
    options.push({
      id: OPTION_IDS.LAST_SIGN_OUT,
      label: t('Last Sign Out'),
      value: latestOperatorRunEndAt ?? t('Unavailable'),
      disabled: latestOperatorRunEndAtOptionDisabled,
    });

    const shiftStartOptionDisabled =
      !shiftStart || safeIsBefore(shiftStart, latestOperatorRunEndAt);
    options.push({
      id: OPTION_IDS.START_OF_SHIFT,
      label: t('Start Of Shift'),
      value: shiftStart ?? t('Unavailable'),
      disabled: shiftStartOptionDisabled,
    });

    return options;
  }, [t, nowMs, latestOperatorRunEndAt, shiftStart]);

  const handleClose = useCallback(() => {
    return onClose();
  }, [onClose]);

  // Handle PIN verification success
  const handlePinSuccess = useCallback(() => {
    setShowPinVerification(false);
    if (pendingSubmitOpts) {
      onSubmit(operator, pendingSubmitOpts);
    }
    setPendingSubmitOpts(null);
  }, [operator, onSubmit, pendingSubmitOpts]);

  // Handle PIN verification cancel
  const handlePinCancel = useCallback(() => {
    setShowPinVerification(false);
    setPendingSubmitOpts(null);
  }, []);

  const handleSelect = useCallback(
    (selectedValue, options) => {
      let submitOpts = null;

      if (options.id === OPTION_IDS.NOW) {
        // Omit the override so SelectOperator stamps a server-anchored now at
        // submit, rather than a client snapshot that can read as a future date.
        submitOpts = {
          pinToPreviousEvent: false,
        };
      } else if (options.id === OPTION_IDS.LAST_SIGN_OUT) {
        submitOpts = {
          pinToPreviousEvent: false,
          startAtOverride: selectedValue,
        };
      } else if (options.id === OPTION_IDS.START_OF_SHIFT) {
        submitOpts = {
          pinToPreviousEvent: false,
          startAtOverride: selectedValue,
        };
      }

      if (!submitOpts) {
        return onClose();
      }

      // Check if PIN verification is required
      if (enableOperatorVerification && operator?.id) {
        setPendingSubmitOpts(submitOpts);
        setShowPinVerification(true);
        return null;
      }

      // No verification required, submit directly
      return onSubmit(operator, submitOpts);
    },
    [onSubmit, operator, onClose, enableOperatorVerification]
  );

  if (!isOpen) return null;

  return (
    <>
      <BaseJobTimeDialog
        headerText={t('What time would you like to login at?')}
        optionsList={optionsList}
        tzid={tzid}
        onClose={handleClose}
        onSelect={handleSelect}
      />

      {enableOperatorVerification && operator?.id && (
        <PinVerificationModal
          isOpen={showPinVerification}
          operatorId={operator.id}
          onSuccess={handlePinSuccess}
          onCancel={handlePinCancel}
        />
      )}
    </>
  );
};

LaborTicketSignInModal.propTypes = {
  operator: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isOpen: PropTypes.bool,
};

export { LaborTicketSignInModal };
